// ---------------------------------------------------------------------------
// Utilidades puras de Mantenimiento: vencimientos, costos, agrupaciones y
// formatos. Sin React ni API, para poder probarlas (lib.test.ts).
// ---------------------------------------------------------------------------
import type { MaintenanceRecord } from "./types";
import { categoryOf, type Category } from "./theme";

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Fecha que manda para vencimientos: agendada o próximo servicio. */
export const dueOf = (r: MaintenanceRecord): string | null =>
  r.scheduledDate || r.nextMaintenanceDate || null;

export const isOpen = (r: MaintenanceRecord) =>
  r.status !== "completado" && r.status !== "cancelado";

/** Inicio del día local de `ms`. */
export function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Días naturales entre hoy y la fecha (negativo = pasado). `null` sin fecha. */
export function daysUntil(value: string | null | undefined, nowMs = Date.now()): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  if (Number.isNaN(t)) return null;
  return Math.round((startOfDay(t) - startOfDay(nowMs)) / DAY_MS);
}

/** Abierto con fecha pasada (vencido). En proceso no cuenta: ya está en taller. */
export function isOverdue(r: MaintenanceRecord, nowMs = Date.now()): boolean {
  if (!isOpen(r) || r.status === "en_proceso") return false;
  const d = daysUntil(dueOf(r), nowMs);
  return d !== null && d < 0;
}

export function isDueWithin(r: MaintenanceRecord, days: number, nowMs = Date.now()): boolean {
  if (!isOpen(r) || r.status === "en_proceso") return false;
  const d = daysUntil(dueOf(r), nowMs);
  return d !== null && d >= 0 && d <= days;
}

/** Costo real de la orden: total desglosado o, si no hay, el costo capturado. */
export const costOf = (r: MaintenanceRecord): number =>
  Number(r.totalCost || 0) || Number(r.cost || 0);

/** Fecha de referencia para costos e historial (cierre, servicio o apertura). */
export const serviceDateOf = (r: MaintenanceRecord): string | null =>
  r.completedDate || r.closedAt || r.lastMaintenanceDate || r.openedAt || null;

/** Clave `YYYY-MM` local. */
export function monthKey(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Últimos `n` meses (claves `YYYY-MM`, del más viejo al actual). */
export function lastMonths(n: number, nowMs = Date.now()): string[] {
  const out: string[] = [];
  const d = new Date(nowMs);
  d.setDate(1);
  for (let i = n - 1; i >= 0; i -= 1) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

/** Costo de los completados por mes, separado en propio y externo. */
export function costByMonth(rows: MaintenanceRecord[], months: string[]) {
  const base = Object.fromEntries(months.map((m) => [m, { month: m, propio: 0, externo: 0, total: 0, count: 0 }]));
  for (const r of rows) {
    if (r.status !== "completado") continue;
    const key = monthKey(serviceDateOf(r));
    if (!key || !base[key]) continue;
    const c = costOf(r);
    const mode = r.workshopMode === "externo" ? "externo" : "propio";
    base[key][mode] += c;
    base[key].total += c;
    base[key].count += 1;
  }
  return months.map((m) => base[m]);
}

export function costByCategory(rows: MaintenanceRecord[]): { category: Category; cost: number; count: number }[] {
  const acc = new Map<Category, { cost: number; count: number }>();
  for (const r of rows) {
    if (r.status !== "completado") continue;
    const cat = categoryOf(r.maintenanceDescription);
    const cur = acc.get(cat) || { cost: 0, count: 0 };
    cur.cost += costOf(r);
    cur.count += 1;
    acc.set(cat, cur);
  }
  return [...acc.entries()].map(([category, v]) => ({ category, ...v })).sort((a, b) => b.cost - a.cost);
}

export function costByVehicle(rows: MaintenanceRecord[]): { vehicleId: string; cost: number; count: number }[] {
  const acc = new Map<string, { cost: number; count: number }>();
  for (const r of rows) {
    if (r.status !== "completado") continue;
    const cur = acc.get(r.vehicleId) || { cost: 0, count: 0 };
    cur.cost += costOf(r);
    cur.count += 1;
    acc.set(r.vehicleId, cur);
  }
  return [...acc.entries()].map(([vehicleId, v]) => ({ vehicleId, ...v })).sort((a, b) => b.cost - a.cost);
}

export function countBy<T>(rows: T[], key: (r: T) => string | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const k = key(r);
    if (!k) continue;
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

/** Prioridad: vencidos (más viejos primero), luego por fecha más cercana. */
export function priorityList(rows: MaintenanceRecord[], withinDays = 7, nowMs = Date.now()): MaintenanceRecord[] {
  return rows
    .filter((r) => isOverdue(r, nowMs) || isDueWithin(r, withinDays, nowMs))
    .sort((a, b) => Date.parse(dueOf(a) || "") - Date.parse(dueOf(b) || ""));
}

/** Unidades con una orden en proceso (fuera de operación). */
export function vehiclesInShop(rows: MaintenanceRecord[]): Set<string> {
  return new Set(rows.filter((r) => r.status === "en_proceso").map((r) => r.vehicleId));
}

/** Texto de búsqueda libre: descripción, taller, mecánico y unidad. */
export function matchesQuery(r: MaintenanceRecord, query: string, vehicleLabel = ""): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [r.maintenanceDescription, r.workshopName, r.assignedMechanic, r.diagnosis, r.requestedBy, vehicleLabel]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
}

/** Todas las fotos de una orden: evidencias + adjuntos que son imagen. */
export function photosOf(r: MaintenanceRecord): string[] {
  const out = [...(r.photos || [])];
  for (const a of r.attachments || []) {
    if (a.url && /\.(webp|jpe?g|png|gif|avif)(\?|$)/i.test(a.url) && !out.includes(a.url)) out.push(a.url);
  }
  return out;
}

/** Miniatura de una foto del Sandbox (`x.webp` → `x-thumb.webp`); otras URLs igual. */
export function thumbOf(url: string): string {
  return /^\/sandbox\/.+\.webp$/.test(url) && !url.endsWith("-thumb.webp") ? url.replace(/\.webp$/, "-thumb.webp") : url;
}

// --- Formatos ---------------------------------------------------------------
export function fmtMoney(n: number | null | undefined, locale = "es-MX", compact = false): string {
  const v = Number(n || 0);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: compact || Math.abs(v) >= 1000 ? 0 : 2,
    notation: compact ? "compact" : "standard",
  }).format(v);
}

export function fmtDate(value: string | null | undefined, locale = "es-MX", withTime = false): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function fmtTime(value: string | null | undefined, locale = "es-MX"): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/** «hace 3 días», «en 2 horas»… con Intl (sin claves de i18n). */
export function fmtRelative(value: string | null | undefined, locale = "es-MX", nowMs = Date.now()): string {
  if (!value) return "—";
  const t = Date.parse(value);
  if (Number.isNaN(t)) return "—";
  const diff = t - nowMs;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60 * 60 * 1000) return rtf.format(Math.round(diff / 60000), "minute");
  if (abs < DAY_MS) return rtf.format(Math.round(diff / 3600000), "hour");
  const days = daysUntil(value, nowMs) ?? 0;
  if (Math.abs(days) < 30) return rtf.format(days, "day");
  if (Math.abs(days) < 365) return rtf.format(Math.round(days / 30), "month");
  return rtf.format(Math.round(days / 365), "year");
}

/** Etiqueta corta de un mes `YYYY-MM` («oct 26»). */
export function fmtMonth(key: string, locale = "es-MX"): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: "short", year: "2-digit" });
}

/** Locale de Intl a partir del idioma de i18next. */
export const intlLocale = (lang?: string) => (lang && lang.toLowerCase().startsWith("en") ? "en-US" : "es-MX");
